import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Coffee, Zap, BookOpen, Star, Loader2, Dice5, Bookmark, ChevronDown, Crown, User, LogOut, History, FileText } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import ReplaceResumeButton from '../components/ReplaceResumeButton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
// import FloatingCoach from '../components/FloatingCoach';
import QuoteCarousel from '../components/QuoteCarousel';
import NonLinearPaths from '../components/NonLinearPaths';
import BookmarkFlyAnimation from '../components/BookmarkFlyAnimation';
import { useSavedRoles } from '../hooks/useSavedRoles';

// Floating Icon Component with parallax effect
const FloatingIcon = ({ icon, initialX, initialY, speed, rotation, scale, scrollY }) => {
  const translateY = scrollY * speed * -0.3;

  return (
    <div
      className="absolute pointer-events-none select-none transition-transform duration-100 ease-out"
      style={{
        left: `${initialX}%`,
        top: `${initialY}%`,
        transform: `translateY(${translateY}px) rotate(${rotation}deg) scale(${scale})`,
        filter: 'drop-shadow(4px 8px 12px rgba(0,0,0,0.15))',
        fontSize: '3rem',
        zIndex: 10,
      }}
      data-speed={speed}
      data-initial-x={initialX}
      data-initial-y={initialY}
    >
      {icon}
    </div>
  );
};

// Categorized vibes for the dropdown and random selection
const VIBE_CATEGORIES = {
  "Identity & Energy": [
    "main character energy but make it sustainable",
    "anxious overachiever seeking peace",
    "chaotic good with a paycheck",
    "introvert who secretly runs things",
    "recovering perfectionist",
    "quiet achiever energy",
    "ambitious but not annoying about it",
    "type A personality in remission",
    "soft life but still ambitious",
    "low-key competitive"
  ],
  "Work Style": [
    "deep work, no meetings",
    "async communication only please",
    "work from a cabin in the woods",
    "laptop and coffee shop forever",
    "4-day work week or bust",
    "no small talk required",
    "headphones in, world out",
    "work alone but feel connected",
    "structured chaos specialist",
    "flow state chaser"
  ],
  "Aesthetic Vibes": [
    "corporate clean girl who cares about climate",
    "dark academia but well-compensated",
    "cottagecore with WiFi",
    "cyber-goth problem solver",
    "cozy job with big impact",
    "minimalist lifestyle, maximalist career",
    "goblin mode but make it professional",
    "solarpunk energy",
    "studio ghibli work vibes",
    "brooklyn creative but midwest stable"
  ],
  "Values & Purpose": [
    "fix broken systems for a living",
    "help people without burning out",
    "save the planet without being broke",
    "make tech more human",
    "fight for the little guy",
    "ethical capitalism (if that exists)",
    "community over corporate",
    "future generations will thank me",
    "be the change but also pay rent",
    "meaningful work that actually pays"
  ],
  "Skills Reframed": [
    "spreadsheet sorcerer",
    "words person, not numbers person",
    "visual thinker seeking outlet",
    "research rabbit hole professional",
    "pattern recognition obsessed",
    "connecting dots nobody else sees",
    "explaining complex things simply",
    "making boring things beautiful",
    "organizing other people's chaos",
    "translating between humans and machines"
  ],
  "Schedule & Lifestyle": [
    "night owl friendly careers",
    "early bird gets the remote job",
    "seasonal work, year-round adventures",
    "travel without the hustle culture",
    "parent-friendly flexibility",
    "side quest friendly career",
    "sabbatical-compatible jobs",
    "nomad-proof income",
    "work hard disappear harder",
    "coast mode enabled"
  ],
  "Industry Adjacent": [
    "tech-adjacent but human-centered",
    "healthcare without the hospital",
    "creative industry but stable income",
    "science without the PhD",
    "business but make it ethical",
    "finance for people who hate finance bros",
    "legal-adjacent without law school",
    "education outside the classroom",
    "media without the toxic culture",
    "entertainment industry's calm corner"
  ],
  "Personality Matches": [
    "jobs for people who notice everything",
    "careers for chronic overthinkers",
    "work for people who hate being managed",
    "roles for professional devil's advocates",
    "jobs for people who read the whole terms of service",
    "careers for former gifted kids",
    "work for people who peaked in the group project",
    "jobs for recovering people pleasers",
    "roles for constructive pessimists",
    "careers for professional skeptics"
  ],
  "Specific Energy": [
    "marie kondo my career",
    "mr. rogers energy with modern pay",
    "leslie knope drive without the chaos",
    "anthony bourdain vibes but alive",
    "bob ross calm in corporate form",
    "david attenborough narrator energy",
    "myth busters curiosity as a job",
    "lego instructions precision work",
    "wikipedia rabbit hole as career",
    "podcast brain employment"
  ]
};

// Flatten all vibes for random selection
const ALL_VIBES = Object.values(VIBE_CATEGORIES).flat();

// Floating icons configuration - easily configurable
const floatingIconsConfig = [
  { icon: '🚀', initialX: 5, initialY: 15, speed: 0.8, rotation: -15, scale: 1.2 },
  { icon: '💼', initialX: 88, initialY: 8, speed: 1.2, rotation: 12, scale: 1.1 },
  { icon: '🎯', initialX: 92, initialY: 45, speed: 0.6, rotation: -8, scale: 1.0 },
  { icon: '💎', initialX: 8, initialY: 55, speed: 1.0, rotation: 20, scale: 1.3 },
  { icon: '🌟', initialX: 15, initialY: 85, speed: 0.9, rotation: -12, scale: 1.1 },
  { icon: '🎨', initialX: 85, initialY: 75, speed: 0.7, rotation: 15, scale: 1.0 },
  { icon: '📈', initialX: 50, initialY: 5, speed: 1.1, rotation: -5, scale: 0.9 },
  { icon: '🔮', initialX: 3, initialY: 35, speed: 0.5, rotation: 10, scale: 1.15 },
  { icon: '✨', initialX: 95, initialY: 25, speed: 0.85, rotation: -18, scale: 1.0 },
  { icon: '🎪', initialX: 78, initialY: 88, speed: 1.3, rotation: 8, scale: 1.2 },
  { icon: '🏆', initialX: 25, initialY: 92, speed: 0.75, rotation: -10, scale: 1.1 },
  { icon: '💡', initialX: 70, initialY: 12, speed: 0.95, rotation: 22, scale: 1.0 },
];

const DiscoverRolesPage = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategoriesRaw] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('discover_categories') || '[]'); } catch { return []; }
  });
  const setCategories = (value) => {
    setCategoriesRaw(prev => {
      const next = typeof value === 'function' ? value(prev) : value;
      try { sessionStorage.setItem('discover_categories', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [error, setError] = useState(null);
  const [customVibe, setCustomVibe] = useState('');
  const [generatingVibe, setGeneratingVibe] = useState(false);
  const { savedRoles, saveRole, removeRole, isSaved } = useSavedRoles(user);
  const [bookmarkAnim, setBookmarkAnim] = useState({ show: false, x: 0, y: 0 });
  const [showResumeDialog, setShowResumeDialog] = useState(false);

  const handleSignOut = async () => { await signOut(); navigate('/'); };
  const [roadmapData, setRoadmapData] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  // If we already have cached categories from sessionStorage, treat as loaded to avoid auto-refetch
  const [categoriesLoaded, setCategoriesLoaded] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('discover_categories') || '[]').length > 0; } catch { return false; }
  });
  const [showVibeDropdown, setShowVibeDropdown] = useState(false);
  const vibeDropdownRef = useRef(null);
  const heroRef = useRef(null);
  const rafRef = useRef(null);
  const resultsRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

  // Scroll handler with requestAnimationFrame for smooth performance
  const handleScroll = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      setScrollY(window.scrollY);
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleScroll]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vibeDropdownRef.current && !vibeDropdownRef.current.contains(event.target)) {
        setShowVibeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Load roadmap data and discovered categories
    if (user) {
        fetchDiscoveredCategories();
        fetchRoadmapContext();
    }
  }, [user]);


  // Fetch previously discovered categories from Supabase
  const fetchDiscoveredCategories = async () => {
      try {
          const { data, error } = await supabase
            .from('discovered_categories')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

          if (error) {
              console.error("Failed to fetch discovered categories", error);
              return;
          }

          if (data && data.length > 0) {
              // Reconstruct categories from saved data
              const loadedCategories = data.map(d => d.category_data);
              setCategories(loadedCategories);
          }
          setCategoriesLoaded(true);
      } catch (e) {
          console.error("Failed to load discovered categories", e);
          setCategoriesLoaded(true);
      }
  };

  // Save a new category to Supabase
  const saveDiscoveredCategory = async (category, vibe) => {
      if (!user) return;

      try {
          const { error } = await supabase
            .from('discovered_categories')
            .insert({
                user_id: user.id,
                vibe: vibe || 'initial',
                category_data: category
            });

          if (error) {
              console.error("Failed to save discovered category", error);
          }
      } catch (e) {
          console.error("Failed to save category to Supabase", e);
      }
  };

  // Clear all discovered categories from Supabase
  const clearDiscoveredCategories = async () => {
      if (!user) return;

      try {
          const { error } = await supabase
            .from('discovered_categories')
            .delete()
            .eq('user_id', user.id);

          if (error) {
              console.error("Failed to clear discovered categories", error);
              toast.error("Failed to clear history");
              return;
          }

          setCategories([]);
          try { sessionStorage.removeItem('discover_categories'); } catch {}
          toast.success("History cleared");
      } catch (e) {
          console.error("Failed to clear categories from Supabase", e);
          toast.error("Failed to clear history");
      }
  };

  const fetchRoadmapContext = async () => {
      try {
          const { data } = await supabase
            .from('career_roadmaps')
            .select('form_data')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (data) {
              setRoadmapData(data.form_data);
          }
      } catch (e) {
          console.error("Failed to load roadmap context", e);
      }
  };

  // Trigger initial fetch only when categories are loaded from Supabase but empty
  useEffect(() => {
    if (categoriesLoaded && categories.length === 0 && roadmapData) {
      fetchDiscoverRoles(null, roadmapData);
    }
  }, [categoriesLoaded, roadmapData]);

  const toggleSaveRole = (e, role) => {
    e.stopPropagation();
    if (isSaved(role.title)) {
      removeRole(role.title);
    } else {
      saveRole(role);
      // Fly animation
      const rect = e?.currentTarget?.getBoundingClientRect?.();
      const x = rect ? rect.left + rect.width / 2 : e?.clientX ?? window.innerWidth / 2;
      const y = rect ? rect.top : e?.clientY ?? 100;
      setBookmarkAnim({ show: true, x, y });
    }
  };

  const handleLucky = () => {
    const randomVibe = ALL_VIBES[Math.floor(Math.random() * ALL_VIBES.length)];
    setCustomVibe(randomVibe);
  };

  const handleVibeSelect = (vibe) => {
    setCustomVibe(vibe);
    setShowVibeDropdown(false);
  };

  const fetchDiscoverRoles = async (vibe = null, forcedContext = null) => {
    try {
      if (vibe) {
        setGeneratingVibe(true);
      } else {
        setLoading(true);
      }
      
      const context = forcedContext || roadmapData || {};
      
      const response = await axios.post(`${BACKEND_URL}/api/discover-roles`, {
        resume_data: context.resume_data,
        interests: context.interests,
        skills: context.skills,
        values: context.values, // Note: roadmapData format might differ slightly from old onboardingData, verify keys
        custom_vibe: vibe
      });

      if (response.data && response.data.categories) {
        if (vibe) {
          // Prepend the new custom category
          setCategories(prev => [...response.data.categories, ...prev]);
          setCustomVibe('');
          // Save each new category to Supabase
          for (const category of response.data.categories) {
            await saveDiscoveredCategory(category, vibe);
          }
          // Scroll to results after a brief delay to let DOM update
          setTimeout(() => {
            resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        } else {
          setCategories(response.data.categories);
          // Save initial categories to Supabase
          for (const category of response.data.categories) {
            await saveDiscoveredCategory(category, null);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching discover roles:', err);
      setError('Failed to load role suggestions. Please try again.');
      toast.error('Failed to load suggestions');
    } finally {
      setLoading(false);
      setGeneratingVibe(false);
    }
  };

  const handleVibeSubmit = (e) => {
    e.preventDefault();
    if (!customVibe.trim()) return;
    fetchDiscoverRoles(customVibe);
  };

  const getCategoryIcon = (title) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('introvert')) return <BookOpen className="w-8 h-8 text-white" />;
    if (lowerTitle.includes('main-character')) return <Crown className="w-8 h-8 text-white" />;
    if (lowerTitle.includes('chaos')) return <Coffee className="w-8 h-8 text-white" />;
    if (lowerTitle.includes('weekend')) return <Heart className="w-8 h-8 text-white" />;
    if (lowerTitle.includes('writing')) return <Zap className="w-8 h-8 text-white" />;
    return <Star className="w-8 h-8 text-white" />;
  };

  const getCategoryColor = (idx) => {
    const colors = [
      'bg-[#FF4B91]', // Pink
      'bg-[#4B91FF]', // Blue
      'bg-[#FFB04B]', // Orange
      'bg-[#91FF4B]', // Green
      'bg-[#B04BFF]', // Purple
    ];
    return colors[idx % colors.length];
  };

  return (
    <div className="w-full min-h-screen font-sans overflow-x-hidden relative"
         style={{
           fontFamily: "'Montserrat', sans-serif",
         }}>
      {/* Fixed background layer - GPU accelerated */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/bluewhite.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: -1,
          transform: 'translateZ(0)',
        }}
      />
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Inter:wght@400;500;600&display=swap');
          .wrapped-text-shadow {
            text-shadow: 4px 4px 0px rgba(0,0,0,0.2);
          }
          .card-hover-effect:hover {
            transform: translateY(-8px) rotate(1deg);
            box-shadow: 12px 12px 0px rgba(0,0,0,0.1);
          }
          .hero-headline {
            font-family: 'Montserrat', sans-serif;
            font-weight: 900;
            letter-spacing: -0.04em;
            line-height: 1.0;
          }
          @keyframes float-gentle {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          .floating-icon {
            animation: float-gentle 4s ease-in-out infinite;
          }
        `}
      </style>

      {/* ── Sticky Header (same style as RoadmapPage) ── */}
      <header className="border-b fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{
        borderColor: 'rgba(255, 255, 255, 0.3)',
        background: 'rgba(255, 252, 248, 0.65)',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.08)',
        WebkitBackdropFilter: 'blur(20px)',
        backdropFilter: 'blur(20px)'
      }}>
        <div className="container mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Left — Logo */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/roadmap')}>
              <img
                src="https://customer-assets.emergentagent.com/job_path-explorer-12/artifacts/cyj4b8vo_Fig%20Icon%202.png"
                alt="Fig Logo"
                className="w-8 h-8 object-contain"
              />
              <h1 className="text-lg sm:text-xl hidden sm:block" style={{
                fontFamily: "'Chango', cursive",
                fontWeight: 400,
                color: '#2d1b0e',
                letterSpacing: '0.04em'
              }}>
                Fig Careers
              </h1>
            </div>

            {/* Center — Spill Tea */}
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (user) {
                  const { count } = await supabase.from('spill_tea_reviews').select('id', { count: 'exact' }).eq('user_id', user.id);
                  navigate(count && count > 0 ? '/spill-tea/home' : '/spill-tea');
                } else {
                  navigate('/spill-tea');
                }
              }}
              className="gap-1.5"
              style={{
                borderColor: 'rgba(37, 99, 235, 0.4)',
                color: '#2563eb',
                background: 'linear-gradient(135deg, rgba(245, 250, 255, 0.8), rgba(232, 243, 255, 0.6))',
                fontFamily: "'Inter', sans-serif",
                fontWeight: '600',
                fontSize: '0.8rem'
              }}
            >
              <span style={{ fontSize: '1rem' }}>🧋</span>
              <span className="hidden sm:inline">Spill Tea</span>
            </Button>

            {/* Right — Actions + User */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/roadmap')}
                className="gap-2"
                style={{
                  borderColor: 'rgba(45, 27, 14, 0.2)',
                  color: '#2d1b0e',
                  background: 'rgba(255, 250, 245, 0.6)',
                  fontFamily: "'Inter', sans-serif"
                }}
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden lg:inline">Roadmap</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('https://buymeacoffee.com/figcareer', '_blank')}
                className="gap-2 hidden md:flex"
                style={{
                  borderColor: '#FFDD00',
                  color: '#000000',
                  background: '#FFDD00',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: '600'
                }}
              >
                <Coffee className="w-4 h-4" />
                <span>Support Us</span>
              </Button>

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2" style={{
                      borderColor: 'rgba(45, 27, 14, 0.2)',
                      color: '#2d1b0e',
                      background: 'rgba(255, 250, 245, 0.6)',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      <User className="w-4 h-4" />
                      <span className="hidden lg:inline">{user.email}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate('/saved')}>
                      <History className="w-4 h-4 mr-2" />
                      Saved Roadmaps & Careers
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setShowResumeDialog(true); }}>
                      <FileText className="w-4 h-4 mr-2" />
                      Replace Resume
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button variant="outline" size="sm" onClick={() => navigate('/connect-email')} style={{
                  borderColor: 'rgba(45, 27, 14, 0.2)',
                  color: '#2d1b0e',
                  background: 'rgba(255, 250, 245, 0.6)'
                }}>
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Floating Icons */}
      <div
        ref={heroRef}
        className="relative w-full min-h-[85vh] overflow-hidden"
        style={{ paddingTop: '64px' }}
      >
        {/* Floating Icons Layer */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingIconsConfig.map((iconConfig, index) => (
            <FloatingIcon
              key={index}
              icon={iconConfig.icon}
              initialX={iconConfig.initialX}
              initialY={iconConfig.initialY}
              speed={iconConfig.speed}
              rotation={iconConfig.rotation}
              scale={iconConfig.scale}
              scrollY={scrollY}
            />
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-[85vh] px-6 py-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="hero-headline text-[#1a1a1a] text-6xl sm:text-7xl md:text-8xl lg:text-9xl mb-6">
              Hidden gems<br />
              <span className="inline-block">found here.</span>
            </h1>
            <p className="text-[#1a1a1a]/80 text-xl md:text-2xl font-medium max-w-2xl mx-auto mb-12 font-['Inter']">
              Discover unique careers you'd be a good fit for, including ones you might not know exist.
            </p>

            {/* Vibe Prompt Input */}
            <div className="max-w-3xl mx-auto">
              <div className="flex flex-col gap-4 items-center justify-center">
                {/* Search Bar Row */}
                <form onSubmit={handleVibeSubmit} className="relative group w-full max-w-xl">
                  <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 rounded-full blur opacity-60 group-hover:opacity-90 transition duration-500"></div>
                  <div className="relative flex items-center">
                    <Input
                      type="text"
                      value={customVibe}
                      onChange={(e) => setCustomVibe(e.target.value)}
                      placeholder="Enter a vibe (e.g., 'Jobs for night owls')..."
                      className="w-full bg-white border-2 border-black/5 text-[#1a1a1a] placeholder:text-gray-500 h-14 pl-6 pr-32 rounded-full text-lg font-medium backdrop-blur-xl focus:ring-2 focus:ring-black/10 font-['Inter'] shadow-xl"
                      disabled={generatingVibe}
                    />
                    <Button
                      type="submit"
                      disabled={generatingVibe || !customVibe.trim()}
                      className="absolute right-2 h-10 rounded-full bg-[#1a1a1a] text-white hover:bg-black/80 font-bold px-6 transition-all font-['Montserrat']"
                    >
                      {generatingVibe ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          GO
                        </>
                      )}
                    </Button>
                  </div>
                </form>

                {/* Buttons Row - Below Search Bar */}
                <div className="flex flex-row gap-3 items-center justify-center">
                  {/* Browse Vibes Dropdown */}
                  <div className="relative" ref={vibeDropdownRef}>
                    <Button
                      onClick={() => setShowVibeDropdown(!showVibeDropdown)}
                      disabled={generatingVibe}
                      className="h-12 px-5 rounded-full bg-white text-[#1a1a1a] font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap border-2 border-black/10 font-['Montserrat'] text-sm"
                    >
                      <ChevronDown className={`w-4 h-4 mr-2 transition-transform ${showVibeDropdown ? 'rotate-180' : ''}`} />
                      BROWSE VIBES
                    </Button>

                    {showVibeDropdown && (
                      <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-80 max-h-96 overflow-y-auto bg-white rounded-2xl shadow-2xl border border-black/10 z-50">
                        {Object.entries(VIBE_CATEGORIES).map(([category, vibes]) => (
                          <div key={category} className="border-b border-black/5 last:border-b-0">
                            <div className="px-4 py-2 bg-black/5 sticky top-0">
                              <span className="text-xs font-black uppercase tracking-wider text-[#1a1a1a]/60 font-['Montserrat']">
                                {category}
                              </span>
                            </div>
                            {vibes.map((vibe, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleVibeSelect(vibe)}
                                className="w-full px-4 py-2 text-left text-sm text-[#1a1a1a] hover:bg-[#1ed760]/10 transition-colors font-['Inter'] font-medium"
                              >
                                {vibe}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleLucky}
                    disabled={generatingVibe}
                    className="h-12 px-5 rounded-full bg-[#1a1a1a] text-white font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap border-none font-['Montserrat'] text-sm"
                  >
                    <Dice5 className="w-4 h-4 mr-2" />
                    SURPRISE ME
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Career Cards Section - Directly below hero, above other sections */}
      <div ref={resultsRef} className="w-full">
        <div className="max-w-7xl mx-auto px-6 py-12">
          {/* Loading State */}
          {(loading || generatingVibe) && (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#1a1a1a] mb-6"></div>
              <p className="text-[#1a1a1a] text-xl font-black animate-pulse uppercase tracking-widest">
                {generatingVibe ? 'Finding Your Hidden Gems...' : 'Vibe Checking Your Career...'}
              </p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && !generatingVibe && (
            <div className="text-center py-12 bg-white/80 rounded-3xl backdrop-blur-md shadow-xl">
              <p className="text-red-600 text-xl font-bold mb-4">{error}</p>
              <Button onClick={() => fetchDiscoverRoles()} className="bg-[#1a1a1a] text-white hover:bg-black/80 font-black px-8 py-4 rounded-full">
                Try Again
              </Button>
            </div>
          )}

          {/* Career Cards - Always rendered when categories exist */}
          {categories.length > 0 && (
            <div className="space-y-24">
              {/* Clear History Button */}
              <div className="flex justify-end mb-8">
                <Button
                  variant="outline"
                  onClick={clearDiscoveredCategories}
                  className="text-[#1a1a1a] border-[#1a1a1a]/30 bg-white/50 hover:bg-white hover:text-[#1a1a1a] hover:border-[#1a1a1a]/50 text-sm font-bold rounded-full px-6"
                >
                  Clear History
                </Button>
              </div>

              {/* Category Cards */}
              {categories.map((category, idx) => (
                <div
                  key={`${category.title}-${idx}`}
                  className="animate-in fade-in slide-in-from-bottom-8 duration-1000"
                  style={{ animationDelay: `${idx * 150}ms` }}
                >
                  {/* Category Header */}
                  <div className="flex items-center gap-6 mb-10 transform -rotate-1">
                    <div className={`p-4 rounded-2xl shadow-[8px_8px_0px_rgba(0,0,0,0.15)] ${getCategoryColor(idx)}`}>
                      {getCategoryIcon(category.title)}
                    </div>
                    <h2 className="text-3xl md:text-5xl font-black text-[#1a1a1a] tracking-tighter uppercase max-w-3xl leading-none" style={{ textShadow: '3px 3px 0px rgba(0,0,0,0.1)' }}>
                      {category.title}
                    </h2>
                  </div>

                  {/* Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {category.roles.map((role, roleIdx) => (
                      <Card
                        key={`${role.title}-${roleIdx}`}
                        className="bg-[#FFF9F0] border-2 border-black/5 text-[#1a1a1a] rounded-[32px] overflow-hidden transition-all duration-300 card-hover-effect shadow-[8px_8px_0px_rgba(0,0,0,0.1)] cursor-pointer"
                        onClick={() => navigate(`/roles/${encodeURIComponent(role.title)}`, { state: { roleData: role } })}
                      >
                        <CardHeader className="pb-4 pt-8 px-8">
                          <div className="flex justify-between items-start mb-4">
                            <Badge className="bg-[#1ed760] text-black hover:bg-[#1ed760] font-black text-sm px-3 py-1 rounded-full uppercase tracking-wide font-['Montserrat']">
                              {role.match_percentage || role.match_score}% Match
                            </Badge>
                            <div className="flex items-center gap-2">
                              {role.salary_range && (
                                <span className="text-sm font-bold text-[#1a1a1a]/80 bg-black/5 px-3 py-1 rounded-full font-['Inter']">
                                  {role.salary_range}
                                </span>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className={`h-8 w-8 rounded-full hover:bg-black/5 ${savedRoles.some(r => r.title === role.title) ? 'text-[#1ed760]' : 'text-gray-400'}`}
                                onClick={(e) => toggleSaveRole(e, role)}
                              >
                                <Bookmark className={`w-5 h-5 ${savedRoles.some(r => r.title === role.title) ? 'fill-current' : ''}`} />
                              </Button>
                            </div>
                          </div>
                          <CardTitle className="text-2xl font-black leading-tight text-[#1a1a1a] font-['Montserrat']">
                            {role.title}
                          </CardTitle>
                          {role.vibe_title && (
                            <p className="text-sm font-bold text-purple-600 italic mt-1 font-['Inter']">
                              aka "{role.vibe_title}"
                            </p>
                          )}
                        </CardHeader>
                        <CardContent className="px-8 pb-8">
                          <div className="space-y-6">
                            <p className="text-base text-[#1a1a1a]/80 font-medium leading-relaxed font-['Inter']">
                              {role.description}
                            </p>

                            {/* Companies Section - NEW */}
                            {role.companies && role.companies.length > 0 && (
                              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-2xl border border-blue-100">
                                <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3 font-['Montserrat']">Where to find this role</p>
                                <div className="flex flex-wrap gap-2">
                                  {role.companies.map((company, i) => (
                                    <span key={i} className="text-sm font-semibold px-3 py-1.5 bg-white text-[#1a1a1a] border border-blue-200 rounded-full font-['Inter'] shadow-sm">
                                      {company}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="bg-white p-4 rounded-2xl border border-black/5">
                              <p className="text-xs font-bold text-[#1ed760] uppercase tracking-widest mb-2 font-['Montserrat']">Why it fits</p>
                              {role.why_it_fits && role.why_it_fits.includes('•') ? (
                                <div className="flex flex-wrap gap-2">
                                  {role.why_it_fits.split('•').map((tag, i) => (
                                    <span key={i} className="text-xs font-bold px-2 py-1 bg-[#FFF9F0] text-[#1a1a1a] border border-black/5 rounded-md font-['Inter']">
                                      {tag.trim()}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-[#1a1a1a] font-bold leading-snug font-['Inter']">
                                  "{role.why_it_fits || "Great match for your profile"}"
                                </p>
                              )}
                            </div>

                            {role.skills && (
                              <div className="flex flex-wrap gap-2 pt-2">
                                {role.skills.slice(0, 3).map((skill, i) => (
                                  <span key={i} className="text-xs font-bold px-3 py-2 bg-black/5 text-[#1a1a1a] rounded-full uppercase tracking-wide font-['Montserrat']">
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quote Carousel Section - Below career cards */}
      <QuoteCarousel />

      {/* Non-Linear Paths Section - At bottom */}
      <NonLinearPaths />

      <BookmarkFlyAnimation
        show={bookmarkAnim.show}
        x={bookmarkAnim.x}
        y={bookmarkAnim.y}
        onComplete={() => setBookmarkAnim({ show: false, x: 0, y: 0 })}
      />

      <Dialog open={showResumeDialog} onOpenChange={setShowResumeDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Resume</DialogTitle>
          </DialogHeader>
          <ReplaceResumeButton onResumeReplaced={() => setShowResumeDialog(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscoverRolesPage;